import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  Tab,
  TextInputV2,
} from '@equip.health/ui';

import { useHistory } from 'react-router-dom';

import {
  GlobalSearchAdminTabs,
  GlobalSearchAdminTabsCollection,
} from './constants';
import AdminSearchResults from './SearchResultsAdmin';
import { useEffect, useRef, useState } from 'react';
import { useLazyGetAllProvidersV2Query } from '~/store/services/providers';
import { debounce } from 'lodash';

const TOP_LEVEL_ID = 'global-search-admin';
const pageSize = 15;

const GlobalSearchAdmin = () => {
  const [selectedTab, setSelectedTab] = useState<GlobalSearchAdminTabs>(
    GlobalSearchAdminTabs.USERS,
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [allProviders, setAllProviders] = useState<GetProviderByIdResponse[]>(
    [],
  );
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const [listIsVisible, setIsListVisible] = useState<boolean>(false);
  const pageNumberRef = useRef(1);

  const history = useHistory();
  const [getAllProviderDetails, { data: suggestions, isFetching }] =
    useLazyGetAllProvidersV2Query();

  useEffect(() => {
    if (pageNumberRef.current === 1) {
      setAllProviders(suggestions?.data || []);
    } else if (suggestions?.data) {
      setAllProviders((prevProviders) => [
        ...prevProviders,
        ...suggestions.data,
      ]);
    }
  }, [suggestions]);

  const handleSelect = (option: GetProviderByIdResponse) => {
    const { basePath } = GlobalSearchAdminTabsCollection[selectedTab];
    history.push(`${basePath}/${option.userId}/info`);
    setIsListVisible(false);
    setSearchTerm('');
  };

  const resultCounts = {
    [GlobalSearchAdminTabs.USERS]: suggestions?.count,
  };

  const fetchMoreProviders = async () => {
    if (suggestions && pageNumberRef.current * pageSize < suggestions.count) {
      const newPageNumber = pageNumberRef.current + 1;
      pageNumberRef.current = newPageNumber;
      await getAllProviderDetails({
        'include-email-search': true,
        'page-number': newPageNumber,
        'page-size': pageSize,
        'provider-search': searchTerm.trim(),
      });
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!listIsVisible) return;

    switch (e.key) {
      case 'Enter':
      case 'Tab': {
        if (allProviders[highlightedIndex]) {
          handleSelect(allProviders[highlightedIndex]);
        }
        e.preventDefault();
        break;
      }
      case 'ArrowUp': {
        setHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex > 0 ? prevIndex - 1 : allProviders.length - 1;
          const elementId = `${TOP_LEVEL_ID}__admin-search-results__option-${allProviders[newIndex].userId}`;
          document.getElementById(elementId)?.scrollIntoView({
            block: newIndex < prevIndex ? 'nearest' : 'start',
            inline: 'nearest',
          });
          return newIndex;
        });
        e.preventDefault();
        break;
      }
      case 'ArrowDown': {
        setHighlightedIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % allProviders.length;
          if (newIndex === 0) {
            fetchMoreProviders();
          }
          const elementId = `${TOP_LEVEL_ID}__admin-search-results__option-${allProviders[newIndex].userId}`;
          document.getElementById(elementId)?.scrollIntoView({
            block: newIndex > prevIndex ? 'nearest' : 'end',
            inline: 'nearest',
          });
          return newIndex;
        });
        e.preventDefault();
        break;
      }
      case 'Escape': {
        setIsListVisible(false);
        setSearchTerm('');
        break;
      }
      default:
        break;
    }
  };

  const listIsOpen = listIsVisible && searchTerm.length > 0;
  const handleOnBlur = () => {
    setTimeout(() => {
      setIsListVisible(false);
      setSearchTerm('');
    }, 300);
  };
  const debouncedFetchOptions = useRef(
    debounce((searchTerm: string) => {
      const trimmedValue = searchTerm.trim();
      if (searchTerm.length === 0) return;
      getAllProviderDetails({
        'include-email-search': true,
        'page-number': pageNumberRef.current,
        'page-size': pageSize,
        'provider-search': trimmedValue,
      });
    }, 500),
  ).current;
  const handleChange = (value: string) => {
    setSearchTerm(value);
    setIsListVisible(true);
    pageNumberRef.current = 1;
    setAllProviders([]);
    debouncedFetchOptions(value);
  };

  return (
    <ChakraBox id={TOP_LEVEL_ID} width="100%">
      <ChakraBox>
        <ChakraBox position="relative" zIndex={2}>
          <TextInputV2
            id={`${TOP_LEVEL_ID}__search-input`}
            leftIcon={FAIconName.searchRegular}
            onBlur={handleOnBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Search"
            value={searchTerm}
          />

          {listIsOpen && (
            <ChakraBox
              background="white"
              border="1px solid"
              borderColor="neutral.line"
              borderRadius="8px"
              display="flex"
              flexDirection="column"
              gap="12px"
              id={`${TOP_LEVEL_ID}__list-popup-container`}
              padding="18px"
              position="absolute"
              top="55px"
              width="420px"
              zIndex={3}
            >
              <ChakraFlex alignItems="center" justifyItems="start">
                {Object.entries(GlobalSearchAdminTabsCollection).map(
                  ([tabKey, { label }]) => {
                    const isActive = selectedTab === tabKey;
                    return (
                      <Tab
                        counter={resultCounts[tabKey]}
                        isActive={isActive}
                        key={tabKey}
                        onClick={() =>
                          setSelectedTab(tabKey as GlobalSearchAdminTabs)
                        }
                        title={label}
                        variant="bubble"
                      />
                    );
                  },
                )}
              </ChakraFlex>
              {selectedTab === GlobalSearchAdminTabs.USERS && (
                <AdminSearchResults
                  fetchMoreProviders={fetchMoreProviders}
                  handleSelect={handleSelect}
                  highlightedIndex={highlightedIndex}
                  id={`${TOP_LEVEL_ID}__admin-search-results`}
                  isLoading={isFetching}
                  maxHeight={420}
                  showUnknownIcon
                  suggestions={allProviders}
                  totalOptionsCount={suggestions?.count}
                />
              )}
            </ChakraBox>
          )}
        </ChakraBox>
      </ChakraBox>
    </ChakraBox>
  );
};

export default GlobalSearchAdmin;
